import {
  Button,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  TextField
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import useNotifier from "@saleor/hooks/useNotifier";
import ImageIcon from "@saleor/icons/Image";
import { OrderDetails_order } from "@saleor/motherOrders/types/OrderDetails";
import { OrderCourierEnum } from "@saleor/motherOrders/utils/enums";
import {
  useRemittanceBulkAllowManualOverride,
  useRemittanceRunnerManualOverride
} from "@saleor/remittanceRunner/mutations";
import { useDialogStyles } from "@saleor/remittanceRunner/styles/dialogStyles";
import { RemittanceMotherOrder } from "@saleor/remittanceRunner/utils/formDataTypes";
import { DialogProps } from "@saleor/types";
import { RemittanceStatusEnum } from "@saleor/types/globalTypes";
import classNames from "classnames";
import React, { useState } from "react";
import Dropzone from "react-dropzone";
import { useIntl } from "react-intl";

import RemittanceManualAdjustmentDropdown from "../RemittanceManualAdjustmentDropdown";

export interface RemittanceStatusOverrideDialogProps extends DialogProps {
  order?: OrderDetails_order;
  refetchOrder?: (variables?: any) => Promise<any>;
  remittanceOrderCourier?: RemittanceMotherOrder | null;
  setRemittanceOrderCourier?: React.Dispatch<
    React.SetStateAction<RemittanceMotherOrder>
  >;
  bulkOrderIds?: string[] | [];
  setBulkOrderIds?: React.Dispatch<React.SetStateAction<string[]>>;
  onRefetchRemittanceOrder?: () => void;
  isFromNonLegacy?: boolean;
}

const fileSizeLimit = 4.0e6;

const RemittanceStatusOverride: React.FC<RemittanceStatusOverrideDialogProps> = props => {
  const {
    open,
    onClose,
    order,
    refetchOrder,
    remittanceOrderCourier,
    setRemittanceOrderCourier,
    bulkOrderIds,
    setBulkOrderIds,
    onRefetchRemittanceOrder,
    isFromNonLegacy
  } = props;

  const classes = useDialogStyles(props);
  const intl = useIntl();
  const notify = useNotifier();

  const [formStatus, setFormStatus] = useState("Remitted");
  const [formReason, setFormReason] = useState("");
  const [imageToUpload, setImageToUpload] = React.useState(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [enableOthersText, setEnableOthersText] = useState(false);
  const [othersValue, setOthersValue] = useState("");

  const isPandagoCourier = () => {
    const courier = isFromNonLegacy
      ? remittanceOrderCourier?.courier
      : order?.courier;

    return (
      courier === OrderCourierEnum.PANDAGO ||
      courier === OrderCourierEnum.PANDAGO_AUTO
    );
  };

  const handleConfirmSubmit = () => {
    if (!formStatus) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "Unable to Status Override Order. Please provide the status for adjustment."
        })
      });
      return;
    }

    if (!formReason || (formReason === "Others" && !othersValue)) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "Unable to Status Override Order. Please provide the reason for adjustment."
        })
      });
      return;
    }

    if (!imageToUpload && !isPandagoCourier) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "Unable to Status Override Order. Please provide the proof of remittance."
        })
      });
      return;
    }

    let overrideReason = formReason;
    if (formReason === "Others") {
      overrideReason += " - " + othersValue.trim();
    }

    if (order?.id) {
      saveManualOverride({
        variables: {
          orderId: order?.id,
          overrideReason,
          overrideStatus: RemittanceStatusEnum.REMITTED,
          receiptImage: imageToUpload
        }
      });
    } else {
      saveRemittanceBulkManualOverride({
        variables: {
          isFromNonLegacy,
          orderIds: bulkOrderIds,
          overrideReason,
          overrideStatus: RemittanceStatusEnum.REMITTED,
          receiptImage: imageToUpload
        }
      });
    }
  };

  const [saveManualOverride] = useRemittanceRunnerManualOverride({
    onCompleted: data => {
      if (data?.createRemittanceManualOverride?.errors?.length === 0) {
        notify({
          status: "success",
          text: "Order Successfully Override"
        });
        resetForm();
        onClose();
        refetchOrder();
      } else if (data?.createRemittanceManualOverride?.errors?.length > 0) {
        data.createRemittanceManualOverride.errors.forEach(error => {
          notify({
            status: "error",
            text: error.message
          });
        });
      }
    },
    onError: errors => {
      const [error] = errors.graphQLErrors;

      if (error?.message) {
        notify({
          status: "error",
          text: error?.message
        });
      } else if (errors[0]?.message) {
        notify({
          status: "error",
          text: errors[0]?.message
        });
      }
    }
  });

  const [
    saveRemittanceBulkManualOverride
  ] = useRemittanceBulkAllowManualOverride({
    onCompleted: data => {
      if (data?.remittanceBulkAllowManualOverride?.errors?.length === 0) {
        notify({
          status: "success",
          text: "Order Successfully Override"
        });
        onRefetchRemittanceOrder();
        resetForm();
        onClose();
        setBulkOrderIds([]);
        setRemittanceOrderCourier(prev => ({
          ...prev,
          courier: "",
          orderCode: ""
        }));
      } else if (data?.remittanceBulkAllowManualOverride?.errors?.length > 0) {
        const errorMessages = data.remittanceBulkAllowManualOverride.errors
          .map(error => error.message)
          .join(", ");
        notify({
          status: "error",
          text: errorMessages
        });
      }
    },
    onError: error => {
      notify({
        status: "error",
        text: error.message
      });
    }
  });

  const resetForm = () => {
    setFormStatus("Remitted");
    setFormReason("");
    setImageToUpload(null);
    setIsSaving(false);
    setEnableOthersText(false);
    setOthersValue("");
  };

  React.useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open]);

  const selectStatus = [
    {
      label: "Remitted",
      value: "Remitted"
    }
  ];
  const selectReason = [
    {
      label: "Bank is Offline",
      value: "Bank is Offline"
    },
    {
      label: "Bank has no operations",
      value: "Bank has no operations"
    },
    {
      label: "Technical Errors (Error in Kiosks, System , etc)",
      value: "Technical Errors (Error in Kiosks, System , etc)"
    },
    {
      label: "Others",
      value: "Others"
    }
  ];

  const checkFile = e => {
    const file = e[0];
    setIsSaving(true);
    const isValidFileType =
      file.type.includes("image/jpeg") ||
      file.type.includes("image/jpg") ||
      file.type.includes("image/png");
    const isValidFileSize = file.size < fileSizeLimit;

    if (!isValidFileType) {
      notify({
        status: "error",
        text: "The format is not acceptable. Check the format and upload again!"
      });
      setIsSaving(false);
    } else if (!isValidFileSize) {
      notify({
        status: "error",
        text: "Image size is too big! Try compressing."
      });
      setIsSaving(false);
    } else {
      setImageToUpload(file);
    }
  };

  const getContentToRender = () => {
    let contentToRender;

    if (imageToUpload !== undefined && imageToUpload !== null) {
      contentToRender = (
        <div className={`${classes.imageContainer} ${classes.cursorHand}`}>
          <img
            alt={"imgToUpload"}
            className={classes.image}
            src={URL.createObjectURL(imageToUpload)}
          />
        </div>
      );
    } else if (isSaving) {
      contentToRender = (
        <div className={`${classes.imageContainer} ${classes.centerItems}`}>
          <CircularProgress className={classes.image} size={32} />
        </div>
      );
    } else {
      contentToRender = (
        <div className={classes.uploadRoot}>
          <ImageIcon className={classes.photosIcon} />
          <p className={classes.remittanceDescriptionLabel}>
            DROP OR CLICK HERE TO UPLOAD
          </p>
          <p className={classes.remittanceSpiel}>
            (Maximum allowed file size is 4MB)
            <br />
            (Accepts only JPG,JPEG and PNG files)
          </p>
        </div>
      );
    }

    return contentToRender;
  };

  const motherOrderCode = isFromNonLegacy
    ? remittanceOrderCourier?.orderCode
    : order?.orderCode;

  return (
    <Dialog onClose={onClose} open={open} maxWidth="sm" fullWidth>
      <DialogContent className={classes.modal}>
        <IconButton
          aria-label="close"
          onClick={() => {
            onClose();
          }}
          className={classes.modalCloseBtn}
        >
          <CloseIcon />
        </IconButton>

        <div className={classes.marginTop50}>
          <h3 className={`${classes.modalTitleText} ${classes.modalTitleRed}`}>
            Status Override
          </h3>
          <h4 className={classes.modalSubtitle}>
            You are about to override the remittance status for
          </h4>
          <h4 className={classes.modalSubtitle}>
            <span style={{ fontWeight: 600 }}>
              {bulkOrderIds?.length > 1
                ? `${bulkOrderIds.length} Mother Order/s`
                : `${motherOrderCode}`}
            </span>
            . Are you sure you want to proceed?
          </h4>
        </div>
        <div className={classes.modalForm}>
          <RemittanceManualAdjustmentDropdown
            placeholder="Status"
            disabled={false}
            choices={selectStatus}
            name="adjustmentStatus"
            label={intl.formatMessage({
              defaultMessage: "Status"
            })}
            value={formStatus}
            isRequired={false}
            onChange={value => setFormStatus(value.target.value)}
          />
          <RemittanceManualAdjustmentDropdown
            placeholder="Adjustment Reason"
            disabled={false}
            choices={selectReason}
            name="adjustmentReason"
            label={intl.formatMessage({
              defaultMessage: "Adjustment Reason"
            })}
            value={formReason}
            isRequired={true}
            onChange={value => {
              setFormReason(value.target.value);

              if (value.target.value === "Others") {
                setEnableOthersText(true);
              } else {
                setEnableOthersText(false);
                setOthersValue(null);
              }
            }}
          />

          {enableOthersText && (
            <TextField
              className={classes.addTopMargin}
              name="adjustmentOtherReason"
              label="Specify other reason here"
              onChange={value => setOthersValue(value.target.value)}
              fullWidth
              multiline
              rows={3}
            />
          )}

          <h3 className={`${classes.modalTitleTextLeft}`}>
            Upload Proof of Remittance
          </h3>
          <div className={classNames(classes.imageGridContainerBorder)}>
            <CardContent>
              <Dropzone
                onDrop={checkFile}
                accept="image/jpeg, image/jpg, image/png"
                multiple={false}
              >
                {({ getRootProps, getInputProps }) => (
                  <div {...getRootProps()}>
                    <CardContent className={classes.fileUploadField}>
                      <input {...getInputProps()} id="fileUpload" type="file" />

                      {getContentToRender()}
                    </CardContent>
                  </div>
                )}
              </Dropzone>
            </CardContent>
          </div>
        </div>

        <div className={classes.modalBottom}>
          <Button
            id="cancelDialogActionBtn"
            autoFocus
            onClick={() => {
              onClose();
            }}
            color="secondary"
            style={{ marginRight: 10 }}
            className={classes.btnCancel}
          >
            <span>Cancel</span>
          </Button>
          <Button
            disabled={!formReason || (!isPandagoCourier() && !imageToUpload)}
            id="confirmDialogActionBtn"
            autoFocus
            onClick={() => handleConfirmSubmit()}
            variant="contained"
            className={classes.btnYes}
          >
            <span className={classes.btnLblWhite}>Yes</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
RemittanceStatusOverride.displayName = "RemittanceStatusOverride";
export default RemittanceStatusOverride;
